import React, { useState, useEffect, useMemo } from 'react';
import ShellLayout from "../../../components/layoutComponents/shellLayout/ShellLayout";
import { Container, Title, Space, Flex, useMantineTheme, Stack, Box, Text } from "@mantine/core";
import OrderTable from "../../../components/pageComponents/orders/OrderTable";
import OrderFilters, { OrderFilterValues } from "../../../components/pageComponents/orders/OrderFilters";
import OrderStats from "../../../components/pageComponents/orders/OrderStats";
import { useLazyGetOrdersQuery, useLazySyncMexalQuery } from "../../../services/OrderService";
import { useLazyGetArticleDetailsQuery } from "../../../services/ArticleService";
import ConnectionStatus from "../../../components/layoutComponents/connectionStatus/ConnectionStatus";
import { OrderDTO } from "../../../interfaces/OrderInterfaces";

// Periodo default per il caricamento ordini (giorni)
// Nota: eBay ha un limite massimo di 90 giorni, usiamo 60 per sicurezza
const DEFAULT_DAYS_RANGE = 60;

// Helper per formattare data in yyyyMMdd
const formatDateMexal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
};

// Helper per ottenere il filtro date default
const getDefaultDateFilter = () => {
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - DEFAULT_DAYS_RANGE);
    startDate.setHours(0, 0, 0, 0);
    
    return {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
    };
};

// Helper per ottenere le date per Mexal sync
const getMexalDateFilter = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - DEFAULT_DAYS_RANGE);
    
    return {
        dateFrom: formatDateMexal(startDate),
        dateTo: formatDateMexal(endDate)
    };
};

const Orders = () => {
    const theme = useMantineTheme();
    const [getOrders, { data: orders, isFetching }] = useLazyGetOrdersQuery();
    const [getArticles, { data: articles }] = useLazyGetArticleDetailsQuery();
    const [syncMexal, { data: mexalDocuments }] = useLazySyncMexalQuery();

    const [filters, setFilters] = useState<OrderFilterValues>({
        marketplaces: [],
        statuses: ['STAGING', 'WAITING_ACCEPTANCE', 'WAITING', 'WAITING_DEBIT', 'WAITING_DEBIT_PAYMENT', 'SHIPPING', 'SHIPPED'], // Default: stati attivi, esclusi RECEIVED, CLOSED, CANCELED, REFUNDED, REFUSED
        search: '',
        startDate: undefined,
        endDate: undefined,
        warnings: []
    });

    // Carica dati all'avvio - ultimi 60 giorni
    useEffect(() => {
        const filter = getDefaultDateFilter();
        console.log('🔍 DEBUG - Parametri richiesta ordini:', filter);
        getOrders(filter);
        getArticles();
        syncMexal(getMexalDateFilter()); // Sincronizza documenti Mexal
    }, []);

    // Debug: Logga gli ordini ricevuti
    useEffect(() => {
        if (orders && orders.length > 0) {
            console.log('📦 DEBUG - Totale ordini ricevuti:', orders.length);
            
            const mediaWorldOrders = orders.filter((o: OrderDTO) => o.marketplace === 'MEDIAWORLD');
            console.log('🛒 DEBUG - Ordini MEDIAWORLD:', mediaWorldOrders.length);
            
            if (mediaWorldOrders.length > 0) {
                // Ordina per data e mostra i primi 5
                const sorted = [...mediaWorldOrders].sort((a, b) => 
                    new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime()
                );
                console.log('📅 DEBUG - Ultimi 5 ordini MEDIAWORLD per data:', 
                    sorted.slice(0, 5).map(o => ({
                        orderId: o.orderId,
                        date: o.creationDate,
                        marketplace: o.marketplace
                    }))
                );
            }
            
            // Mostra anche il range di date totale
            const dates = orders.map((o: OrderDTO) => new Date(o.creationDate).getTime());
            const minDate = new Date(Math.min(...dates));
            const maxDate = new Date(Math.max(...dates));
            console.log('📆 DEBUG - Range date tutti gli ordini:', {
                from: minDate.toLocaleDateString('it-IT'),
                to: maxDate.toLocaleDateString('it-IT')
            });
        }
    }, [orders]);

    // Raggruppa ordini per marketplace
    const ordersByMarketplace = useMemo(() => {
        if (!orders) return {};

        const grouped: Record<string, OrderDTO[]> = {};
        orders.forEach((order: OrderDTO) => {
            if (!grouped[order.marketplace]) {
                grouped[order.marketplace] = [];
            }
            grouped[order.marketplace].push(order);
        });
        return grouped;
    }, [orders]);

    // Applica i filtri lato client e ordina per data decrescente
    const filteredOrders = useMemo(() => {
        if (!orders) return [];

        const filtered = orders.filter((order: OrderDTO) => {
            // Filtro status
            if (filters.statuses.length > 0 && !filters.statuses.includes(order.status)) {
                return false;
            }

            // Filtro ricerca
            if (filters.search) {
                const searchLower = filters.search.toLowerCase();
                const matchesOrderId = order.orderId?.toLowerCase().includes(searchLower);
                const matchesMarketplaceOrderId = order.marketplaceOrderId?.toLowerCase().includes(searchLower);
                const matchesCustomer = 
                    order.customer?.firstName?.toLowerCase().includes(searchLower) ||
                    order.customer?.lastName?.toLowerCase().includes(searchLower) ||
                    order.customer?.email?.toLowerCase().includes(searchLower);
                const matchesSku = order.items?.some(item => 
                    item.sku?.toLowerCase().includes(searchLower) ||
                    item.productName?.toLowerCase().includes(searchLower)
                );
                
                if (!matchesOrderId && !matchesMarketplaceOrderId && !matchesCustomer && !matchesSku) {
                    return false;
                }
            }

            // Filtro date
            if (filters.startDate) {
                const orderDate = new Date(order.creationDate);
                const startDate = new Date(filters.startDate);
                if (orderDate < startDate) return false;
            }
            if (filters.endDate) {
                const orderDate = new Date(order.creationDate);
                const endDate = new Date(filters.endDate);
                endDate.setHours(23, 59, 59, 999);
                if (orderDate > endDate) return false;
            }

            // Filtro warnings
            if (filters.warnings.length > 0) {
                const showWithWarnings = filters.warnings.includes('with_warnings');
                const showWithoutWarnings = filters.warnings.includes('without_warnings');

                if (showWithWarnings && !showWithoutWarnings) {
                    if (!order.hasQuantityWarning) return false;
                } else if (!showWithWarnings && showWithoutWarnings) {
                    if (order.hasQuantityWarning) return false;
                }
            }

            return true;
        });

        // Ordina per data decrescente (più recenti prima)
        return filtered.sort((a, b) => {
            const dateA = new Date(a.creationDate).getTime();
            const dateB = new Date(b.creationDate).getTime();
            return dateB - dateA;
        });
    }, [orders, filters]);

    const handleRefresh = () => {
        getOrders(getDefaultDateFilter());
        getArticles();
        syncMexal(getMexalDateFilter()); // Sincronizza documenti Mexal
    };

    return (
        <ShellLayout
            Body={
                <>
                    <Space h="lg" />
                    <Container size="95%">
                        <Flex justify="space-between" align="center" mb="xl">
                            <Title order={1} fw={500} c={theme.colors.textBlack?.[0] || 'black'}>
                                Ordini
                            </Title>
                            <ConnectionStatus />
                        </Flex>

                        <OrderStats data={filteredOrders} />

                        <OrderFilters
                            filters={filters}
                            setFilters={setFilters}
                            onRefresh={handleRefresh}
                            refreshing={isFetching}
                        />

                        <Stack gap="xl">
                            {Object.entries(ordersByMarketplace)
                                .sort(([a], [b]) => a.localeCompare(b))
                                .map(([marketplace, allOrders]) => {
                                    // Applica filtri per questo marketplace
                                    const marketplaceFilteredOrders = allOrders.filter((order: OrderDTO) => {
                                        // Filtro status
                                        if (filters.statuses.length > 0 && !filters.statuses.includes(order.status)) {
                                            return false;
                                        }

                                        // Filtro ricerca
                                        if (filters.search) {
                                            const searchLower = filters.search.toLowerCase();
                                            const matchesOrderId = order.orderId?.toLowerCase().includes(searchLower);
                                            const matchesMarketplaceOrderId = order.marketplaceOrderId?.toLowerCase().includes(searchLower);
                                            const matchesCustomer = 
                                                order.customer?.firstName?.toLowerCase().includes(searchLower) ||
                                                order.customer?.lastName?.toLowerCase().includes(searchLower) ||
                                                order.customer?.email?.toLowerCase().includes(searchLower);
                                            const matchesSku = order.items?.some(item => 
                                                item.sku?.toLowerCase().includes(searchLower) ||
                                                item.productName?.toLowerCase().includes(searchLower)
                                            );
                                            
                                            if (!matchesOrderId && !matchesMarketplaceOrderId && !matchesCustomer && !matchesSku) {
                                                return false;
                                            }
                                        }

                                        // Filtro date
                                        if (filters.startDate) {
                                            const orderDate = new Date(order.creationDate);
                                            const startDate = new Date(filters.startDate);
                                            if (orderDate < startDate) return false;
                                        }
                                        if (filters.endDate) {
                                            const orderDate = new Date(order.creationDate);
                                            const endDate = new Date(filters.endDate);
                                            endDate.setHours(23, 59, 59, 999);
                                            if (orderDate > endDate) return false;
                                        }

                                        // Filtro warnings
                                        if (filters.warnings.length > 0) {
                                            const showWithWarnings = filters.warnings.includes('with_warnings');
                                            const showWithoutWarnings = filters.warnings.includes('without_warnings');

                                            if (showWithWarnings && !showWithoutWarnings) {
                                                if (!order.hasQuantityWarning) return false;
                                            } else if (!showWithWarnings && showWithoutWarnings) {
                                                if (order.hasQuantityWarning) return false;
                                            }
                                        }

                                        return true;
                                    }).sort((a, b) => {
                                        const dateA = new Date(a.creationDate).getTime();
                                        const dateB = new Date(b.creationDate).getTime();
                                        return dateB - dateA;
                                    });

                                    // Non mostrare marketplace se non ha ordini dopo i filtri
                                    if (marketplaceFilteredOrders.length === 0) return null;

                                    return (
                                        <Box key={marketplace}>
                                            <Flex mb="md" align="center" gap="sm">
                                                <Title order={3} fw={600}>{marketplace}</Title>
                                                <Text size="sm" c="dimmed">({marketplaceFilteredOrders.length} ordini)</Text>
                                            </Flex>
                                            <OrderTable
                                                orders={marketplaceFilteredOrders}
                                                loading={isFetching}
                                                articles={articles}
                                                mexalDocuments={mexalDocuments}
                                            />
                                        </Box>
                                    );
                                })
                            }
                        </Stack>
                    </Container>
                </>
            }
        />
    );
};

export default Orders;
